
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { Star, Calendar, Check, Coins, MapPin, BookOpen, Camera, Share2 } from 'lucide-react';
import { useState } from 'react';
import LoginPrompt from '@/components/shared/LoginPrompt';
import OceanBackground from '@/components/background/OceanBackground';

const tasks = [
  { name: '注册奖励', points: 100, done: true, icon: Star },
  { name: '每日签到', points: 10, done: false, icon: Calendar },
  { name: '创建行程', points: 50, done: true, icon: MapPin },
  { name: '发布日记', points: 30, done: false, icon: BookOpen },
  { name: '分享游记', points: 20, done: false, icon: Share2 },
];

const transactions = [
  { desc: 'AI行程生成', date: '2024-01-15', change: -20, type: 'consume' },
  { desc: '每日签到', date: '2024-01-14', change: 10, type: 'earn' },
  { desc: '注册奖励', date: '2024-01-01', change: 100, type: 'earn' },
];

const weekDays = ['一', '二', '三', '四', '五', '六', '日'];

const PointsCenter = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInDays, setCheckInDays] = useState([true, true, true, false, false, false, false]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-bg-primary text-text-secondary">加载中...</div>;

  if (!user) {
    return (
      <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #041426 0%, #061A2C 50%, #041426 100%)' }}>
        <Navbar />
        <OceanBackground />
        <div className="pt-28 pb-20 relative z-10">
          <LoginPrompt title="积分中心" description="登录后查看积分、完成任务与兑换奖励" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ background: 'linear-gradient(180deg, #041426 0%, #061A2C 50%, #041426 100%)' }}>
      <Navbar />
      <OceanBackground />
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-28 pb-20 relative z-10">
        <Card className="border-0 shadow-card rounded-card bg-gradient-to-br from-primary to-accent text-white mb-8">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="text-sm opacity-90 mb-1">当前积分</p>
                <p className="text-5xl font-bold mb-2">1,250</p>
                <p className="text-sm opacity-90 mb-3">银牌会员</p>
                <div className="w-48 bg-white/20 rounded-full h-2">
                  <div className="bg-white rounded-full h-2" style={{ width: '60%' }} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-6 h-6" />
                <span className="text-lg font-medium">银牌</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="border-0 shadow-card rounded-card bg-white lg:col-span-1">
            <CardContent className="p-5">
              <h3 className="font-medium text-text-primary mb-4">每日签到</h3>
              <div className="grid grid-cols-7 gap-2 mb-4">
                {weekDays.map((d, i) => (
                  <div key={i} className={`aspect-square rounded-smwidget flex flex-col items-center justify-center text-xs ${checkInDays[i] ? 'bg-primary text-white' : 'bg-bg-secondary text-text-secondary'}`}>
                    <span>{d}</span>
                    {checkInDays[i] && <Check className="w-3 h-3 mt-0.5" />}
                  </div>
                ))}
              </div>
              <Button onClick={() => { if (!checkedIn) { setCheckedIn(true); setCheckInDays((p) => { const n = [...p]; n[3] = true; return n; }); } }} disabled={checkedIn} className="w-full bg-primary hover:bg-primary-hover text-white rounded-btn">
                {checkedIn ? '已签到' : '立即签到'}
              </Button>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-card rounded-card bg-white lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="font-medium text-text-primary mb-4">积分任务</h3>
              <div className="space-y-3">
                {tasks.map((t) => (
                  <div key={t.name} className="flex items-center justify-between p-3 rounded-smwidget bg-bg-secondary">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><t.icon className="w-4 h-4 text-primary" /></div>
                      <span className="text-sm text-text-primary">{t.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-primary font-medium">+{t.points}</span>
                      {t.done ? <Badge className="bg-success text-white rounded-full text-xs">已完成</Badge> : <Button size="sm" className="rounded-btn bg-primary hover:bg-primary-hover text-white h-7 text-xs">去完成</Button>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-0 shadow-card rounded-card bg-white">
          <CardContent className="p-5">
            <h3 className="font-medium text-text-primary mb-4">积分流水</h3>
            <Tabs defaultValue="全部">
              <TabsList className="bg-bg-secondary rounded-btn p-1 mb-4">
                {['全部', '获得', '消费'].map((t) => <TabsTrigger key={t} value={t} className="rounded-btn px-4 text-xs data-[state=active]:bg-primary data-[state=active]:text-white">{t}</TabsTrigger>)}
              </TabsList>
              <div className="space-y-3">
                {transactions.map((tr, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-[#E8E4DF] last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tr.type === 'earn' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`}>
                        <Coins className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm text-text-primary">{tr.desc}</p>
                        <p className="text-xs text-text-tertiary">{tr.date}</p>
                      </div>
                    </div>
                    <span className={`text-sm font-bold ${tr.type === 'earn' ? 'text-success' : 'text-danger'}`}>{tr.change > 0 ? '+' : ''}{tr.change}</span>
                  </div>
                ))}
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PointsCenter;

